import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Badge, Button, Card, Spinner } from 'react-bootstrap';
import { getProfile } from '../../api/profile';
import DummyUser from '../../assets/images/avatar.png';
import thumbPlaceholder from '../../assets/video_thumbnail.png';
import './user-profile.scss';

export const StudentDetails = () => {
    const location = useLocation();
    const id = location.state?.id;
    const navigate = useNavigate();
    const [studentData, setStudentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFullBio, setShowFullBio] = useState(false);

    useEffect(() => {
        const fetchStudentProfile = async () => {
            if (!id) return;
            try {
                setLoading(true);
                const res = await getProfile(id);
                const studentUser = res.data || {};
                setStudentData({
                    student: studentUser,
                    courses: res.get_courses || [],
                    my_posts: studentUser.my_posts || res.my_posts || [],
                    posts_count: studentUser.posts_count || res.posts_count || res.total_posts || studentUser.my_posts_count || (studentUser.my_posts || res.my_posts || []).length,
                    upcoming_classes: studentUser.upcoming_classes || res.upcoming_classes || [],
                    followers_count: studentUser.followers_count || res.followers_count || 0,
                    following_count: studentUser.following_count || res.following_count || 0,
                    languages: res.languages || studentUser.languages || [],
                    languages_string: res.languages_string || studentUser.languages_string || '',
                    country: studentUser.country || null,
                    description: studentUser.description || '',
                });
                setError(null);
            } catch (err) {
                console.error('Failed to fetch student details:', err);
                setError(err.response?.data?.message || 'Failed to load student profile');
            } finally {
                setLoading(false);
            }
        };

        fetchStudentProfile();
    }, [id]);

    const handleShare = (platform) => {
        const shareUrl = window.location.href;
        const text = `Check out ${name}'s profile on Yoospeak!`;

        if (platform === 'copy') {
            navigator.clipboard.writeText(shareUrl);
            alert('Link copied to clipboard!');
            return;
        }

        const urls = {
            fb: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
            tw: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
            wa: `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + shareUrl)}`
        };

        if (urls[platform]) window.open(urls[platform], '_blank');
    };

    const toggleBio = () => setShowFullBio(!showFullBio);

    const data = studentData || {
        student: {
            full_name: 'Loading Student...',
            profile_picture: '',
            profile_cover_image: '',
        },
        courses: [],
        my_posts: [],
        upcoming_classes: [],
        followers_count: 0,
        following_count: 0,
        languages: [],
        languages_string: '',
        country: '',
        description: ''
    };

    if (error || (!loading && !studentData)) {
        return (
            <Container className="py-5 text-center">
                <div className="alert alert-danger">{error || 'Student not found'}</div>
                <Link to="/communities" className="btn btn-primary rounded-pill px-4">Back to Communities</Link>
            </Container>
        );
    }

    const {
        student,
        courses,
        my_posts,
        posts_count = data?.posts_count ?? data?.total_posts ?? data?.my_posts_count ?? data?.student?.posts_count ?? my_posts?.length ?? 0,
        upcoming_classes,
        followers_count,
        following_count,
        languages,
        languages_string,
        country,
        description
    } = data;

    const profilePic = student.profile_picture || DummyUser;
    const coverPic = student.profile_cover_image || null;
    const name = student.full_name || 'Student';

    return (
        <section className="teachers-details-page py-4 position-relative">
            {loading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center" style={{
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.4)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    transition: 'all 0.3s ease-in-out'
                }}>
                    <div className="p-4 rounded-4 shadow bg-white bg-opacity-75 text-center d-flex flex-column align-items-center" style={{ border: '1px solid rgba(255, 255, 255, 0.5)' }}>
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <span className="mt-3 fw-bold text-navy" style={{ letterSpacing: '0.5px' }}>Loading profile...</span>
                    </div>
                </div>
            )}
            <Container fluid="xl">
                {coverPic ? (
                    <div className="teacher-cover-image mb-4 rounded-3 overflow-hidden shadow-sm" style={{ height: '240px' }}>
                        <img src={coverPic} alt="Cover" className="w-100 h-100 object-fit-cover" />
                    </div>
                ) : (
                    <div className="teacher-cover-image mb-4 rounded-3 overflow-hidden shadow-sm" style={{
                        height: '200px',
                        background: 'linear-gradient(135deg, #2193F2 0%, #4242F5 55%, #3F13E0 100%)'
                    }}></div>
                )}

                <div className="layout-wrapper">
                    {/* Main Content Column */}
                    <div className="teacher-main-content">
                        {/* Student Header */}
                        <div className="teacher-header text-gray-900 d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                            <div className="teacher-profile d-flex align-items-center gap-4">
                                <div className="profile-img-container position-relative">
                                    <img
                                        src={profilePic}
                                        alt={name}
                                        className="border border-4 border-white rounded-circle shadow-lg"
                                        style={{ height: '100px', width: '100px', objectFit: 'cover' }}
                                        onError={(e) => { e.target.src = DummyUser; }}
                                    />
                                </div>
                                <span className="teacher-info text-capitalize">
                                    <h1 className="teacher-name fw-bold fs-2 text-gray-900 mb-1">{name}</h1>
                                    <div className="teacher-tagline text-gray-600 fs-5">
                                        Active Language Learner
                                    </div>
                                </span>
                            </div>
                            {country && (
                                <div className="country-info d-flex align-items-center gap-2 bg-white px-4 py-2 rounded-pill shadow-sm border">
                                    <span className="country-flag fs-4">🌐</span>
                                    <span className="country-name fw-bold text-gray-700">
                                        {typeof country === 'object' ? country.name : country}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Experience and Level Badges */}
                        <div className="experience-badges text-base text-gray-700 mb-4 d-flex flex-wrap align-items-center gap-2">
                            <Badge bg="info" className="fw-semibold px-4 py-2 rounded-pill">
                                Student Profile
                            </Badge>
                            {student.is_blue_verified && (
                                <Badge bg="primary" className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill shadow-sm">
                                    <i className="bi bi-patch-check-fill"></i> Blue Verified
                                </Badge>
                            )}
                        </div>

                        {/* Info Bar / Stats */}
                        <div className="stats-info-bar bg-white border border-light-subtle d-flex flex-wrap align-items-center justify-content-between gap-3 rounded-4 mb-5 px-4 py-4 shadow-sm">
                            <div className="dynamic-info d-flex align-items-center gap-4">
                                <div className="stat-item text-center">
                                    <div className="fw-black fs-3 text-primary">{followers_count}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Followers</div>
                                </div>
                                <div className="stat-item text-center ps-4 border-start">
                                    <div className="fw-black fs-3 text-primary">{following_count}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Following</div>
                                </div>
                                <div className="stat-item text-center ps-4 border-start">
                                    <div className="fw-black fs-3 text-primary">{posts_count}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Posts</div>
                                </div>
                                <div className="stat-item text-center ps-4 border-start">
                                    <div className="fw-black fs-3 text-primary">{courses.length}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Courses</div>
                                </div>
                            </div>
                        </div>

                        {/* About Me Section */}
                        <div className="about-section mb-5">
                            <div className="main-heading mb-3 fs-4 fw-bold">About me</div>
                            <div className="description-text text-gray-700 bg-white p-4 rounded-4 border shadow-sm">
                                <div className={`bio-content ${!showFullBio && description?.length > 400 ? 'text-truncate-4' : ''}`} style={{ whiteSpace: 'pre-line' }}>
                                    {description || 'No bio details provided yet. Add a brief description in settings.'}
                                </div>
                                {description?.length > 400 && (
                                    <Button variant="link" className="p-0 mt-3 text-decoration-none fw-bold text-primary" onClick={() => setShowFullBio(!showFullBio)}>
                                        {showFullBio ? 'Read less' : 'Read more'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* My Languages Section */}
                        <div className="language-section mb-5">
                            <div className="main-heading mb-3 fs-4 fw-bold">Languages</div>
                            <div className="d-flex flex-wrap gap-3">
                                {languages_string ? languages_string.split(',').map((lang, idx) => (
                                    <div key={idx} className="lang-item bg-white border border-light-subtle d-flex align-items-center gap-3 px-4 py-2 rounded-pill shadow-sm">
                                        <div className="lang-icon bg-primary bg-opacity-10 text-primary rounded-circle flex-center" style={{ width: '32px', height: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <i className="bi bi-translate"></i>
                                        </div>
                                        <span className="lang-name fw-bold text-gray-800">{lang.trim()}</span>
                                    </div>
                                )) : languages.length > 0 ? languages.map((lang, idx) => (
                                    <div key={idx} className="lang-item bg-white border border-light-subtle d-flex align-items-center gap-3 px-4 py-2 rounded-pill shadow-sm">
                                        <div className="lang-icon bg-primary bg-opacity-10 text-primary rounded-circle flex-center" style={{ width: '32px', height: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <i className="bi bi-translate"></i>
                                        </div>
                                        <span className="lang-name fw-bold text-gray-800">{typeof lang === 'object' ? lang.name : lang}</span>
                                    </div>
                                )) : <div className="text-muted">No languages specified.</div>}
                            </div>
                        </div>

                        {/* Enrolled Courses Section */}
                        {courses.length > 0 && (
                            <div className="courses-section mb-5">
                                <div className="main-heading mb-4 fs-4 fw-bold">My Courses</div>
                                <Row className="g-4">
                                    {courses.map((course) => {
                                        const cImage = course.cover_image || course.thumbnail || course.image || 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=500&h=280&fit=crop';

                                        const getProgressPercent = (progress) => {
                                            if (!progress) return 0;
                                            if (typeof progress === 'number') return progress;
                                            if (typeof progress === 'object') {
                                                const total = progress.total_no_of_lesson || 0;
                                                const completed = progress.no_of_completed_lesson || 0;
                                                return total > 0 ? Math.round((completed / total) * 100) : 0;
                                            }
                                            return 0;
                                        };
                                        const progressPercent = getProgressPercent(course.progress);

                                        return (
                                            <Col md={6} key={course._id}>
                                                <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden card-hover" style={{ transition: 'all 0.2s' }}>
                                                    <div style={{ height: '170px', overflow: 'hidden' }}>
                                                        <img
                                                            src={cImage}
                                                            alt={course.title}
                                                            className="w-100 h-100 object-fit-cover"
                                                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=500&h=280&fit=crop'; }}
                                                        />
                                                    </div>
                                                    <Card.Body className="p-4 d-flex flex-column justify-content-between">
                                                        <div>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-1.5 rounded-pill text-xs">
                                                                    {course.categoryName || 'Language'}
                                                                </span>
                                                                <span className="text-base fw-bold text-primary">${course.price || 0}</span>
                                                            </div>
                                                            <h4 className="fs-5 fw-bold text-gray-900 mb-2 text-truncate-2">{course.title}</h4>
                                                            {course.progress !== undefined && (
                                                                <div className="mt-3">
                                                                    <div className="d-flex justify-content-between text-xs text-gray-500 mb-1">
                                                                        <span>Learning Progress</span>
                                                                        <span>{progressPercent}%</span>
                                                                    </div>
                                                                    <div className="progress animate-pulse" style={{ height: '6px', borderRadius: '50px' }}>
                                                                        <div className="progress-bar bg-primary rounded-pill" style={{ width: `${progressPercent}%` }}></div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Button
                                                            variant="primary"
                                                            className="mt-3 w-100 rounded-3 py-2 fw-bold"
                                                            onClick={() => navigate('/course-detail', { state: { id: course._id } })}
                                                        >
                                                            Open Course
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        )}

                        {/* Booked Classes Section */}
                        {upcoming_classes.length > 0 && (
                            <div className="upcoming-classes mb-5">
                                <div className="main-heading mb-4 fs-4 fw-bold">Scheduled Classes</div>
                                <Row className="g-4">
                                    {upcoming_classes.map((cls) => (
                                        <Col md={6} key={cls._id}>
                                            <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden card-hover" style={{ transition: 'all 0.2s' }}>
                                                <div className="bg-primary bg-opacity-10 p-4 pb-2">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <Badge bg={cls.type === 'one_to_one' ? 'info' : 'primary'} className="rounded-pill px-3 py-2">
                                                            {cls.type === 'one_to_one' ? '1-on-1 Session' : 'Group Session'}
                                                        </Badge>
                                                        {cls.cost !== undefined && (
                                                            <span className="fs-4 fw-black text-primary">${cls.cost || 0}</span>
                                                        )}
                                                    </div>
                                                    <h3 className="fs-5 fw-bold text-gray-900 mb-0 text-truncate">{cls.title}</h3>
                                                </div>
                                                <Card.Body className="p-4 pt-0 d-flex flex-column justify-content-between">
                                                    <div className="text-sm text-gray-600 my-3">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <i className="bi bi-calendar3 text-primary"></i>
                                                            <span className="fw-medium">{new Date(cls.start_date).toLocaleDateString()}</span>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bi bi-clock text-primary"></i>
                                                            <span className="fw-medium">{cls.start_time} - {cls.end_time}</span>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="primary"
                                                        className="w-100 rounded-3 py-2 fw-bold mt-auto"
                                                        onClick={() => navigate('/class-detail', { state: { id: cls._id } })}
                                                    >
                                                        Go to Class Details
                                                    </Button>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                        {/* My Posts (Video Feed) */}
                        {my_posts.length > 0 && (
                            <div className="my-posts-section mb-5">
                                <div className="main-heading mb-4 fs-4 fw-bold">Latest Posts</div>
                                <Row className="g-4 row-cols-1 row-cols-sm-2 row-cols-md-3">
                                    {my_posts.map((post) => (
                                        <Col key={post._id}>
                                            <div className="video-card rounded-4 overflow-hidden shadow-sm border h-100 bg-white shadow-hover transition-all">
                                                <div className="ratio ratio-16x9 position-relative cursor-pointer" onClick={() => navigate('/video-feed')}>
                                                    <img
                                                        src={post.thumbnailUrl || thumbPlaceholder}
                                                        alt={post.title}
                                                        className="object-fit-cover w-100 h-100"
                                                        onError={(e) => { e.target.src = thumbPlaceholder; }}
                                                    />
                                                    <div className="play-btn-overlay position-absolute top-50 start-50 translate-middle bg-white bg-opacity-90 rounded-circle d-flex justify-content-center align-items-center shadow-lg" style={{ width: '45px', height: '45px' }}>
                                                        <i className="bi bi-play-fill text-primary fs-3"></i>
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    <div className="fw-bold text-gray-900 text-truncate mb-2">{post.title}</div>
                                                    <div className="text-xs text-gray-500 d-flex justify-content-between align-items-center">
                                                        <span className="d-flex align-items-center gap-1">
                                                            <i className="bi bi-eye"></i> {post.views || 0} views
                                                        </span>
                                                        <span className="bg-light px-2 py-1 rounded">{new Date(post.createdAt).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                    </div>

                    {/* Sidebar Column */}
                    <div className="sidebar-sticky-col">
                        <Card className="sidebar-wrapper border-0 rounded-4 shadow-xl overflow-hidden">
                            <div className="bg-primary text-white p-5 text-center position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #2193F2, #4242F5)' }}>
                                <div className="decoration-circle position-absolute" style={{ top: '-20px', right: '-20px', width: '100px', height: '100px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }}></div>
                                <div className="text-sm text-uppercase fw-black opacity-75 mb-1 tracking-widest">Profile Status</div>
                                <div className="d-flex align-items-center justify-content-center gap-3">
                                    <span className="display-6 fw-black">Verified Learner</span>
                                </div>
                            </div>

                            <Card.Body className="p-4 p-xl-5">
                                <div className="cta-buttons d-flex flex-column gap-3 mb-4">
                                    <Button
                                        className="btn-custom py-3 fs-5 fw-black shadow-lg rounded-3"
                                        onClick={() => navigate(`/communities`)}
                                        style={{ background: 'linear-gradient(135deg, #2193F2, #4242F5)', border: 'none', color: '#fff' }}
                                    >
                                        <i className="bi bi-people-fill me-2"></i> Join Communities
                                    </Button>
                                </div>

                                <hr className="my-5 opacity-10" />

                                <div className="share-section text-center">
                                    <div className="text-xs fw-black text-gray-400 mb-4 text-uppercase tracking-widest">Share this profile</div>
                                    <div className="share-actions d-flex justify-content-center gap-3">
                                        {[
                                            { icon: 'bi-facebook', key: 'fb' },
                                            { icon: 'bi-twitter-x', key: 'tw' },
                                            { icon: 'bi-whatsapp', key: 'wa' },
                                            { icon: 'bi-link-45deg', key: 'copy' }
                                        ].map((soc) => (
                                            <div
                                                key={soc.key}
                                                className="share-icon-circle pointer transition-all shadow-sm rounded-circle d-flex justify-content-center align-items-center"
                                                onClick={() => handleShare(soc.key)}
                                                style={{ width: '40px', height: '40px', background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                                            >
                                                <i className={`bi ${soc.icon} fs-5 text-gray-600`}></i>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </Container>
        </section>
    );
};
